import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import {IonSearchbar, ToastController} from '@ionic/angular';
import {PatientDataProvider} from '../../../providers/patient-data/patient-data';
import {Events} from '../../../providers/events';
import {AddressData} from '../../../interfaces/address-data';
import {GeoPlace} from '../../../interfaces/geo-place-data';
import {ManagementData} from '../../../interfaces/management-data';
import { TranslateService } from '@ngx-translate/core';

/**
 * Generated class for the GoogleMapsComponent component.
 */

declare let google: any;

@Component({
  selector: 'google-maps',
  templateUrl: 'google-maps.html',
  styleUrls: ['./google-maps.scss']
})
export class GoogleMapsComponent implements OnInit {

  @ViewChild('mapCanvas', {static: true}) mapElement: ElementRef<HTMLDivElement>;
  @ViewChild(IonSearchbar, {static: true}) searchbar: IonSearchbar;
  @ViewChild('setButton', {static: true}) setButton: ElementRef<HTMLDivElement>;
  @ViewChild('refreshButton', {static: true}) refreshButton: ElementRef<HTMLDivElement>;

  markers: Array<any> = [];
  map: any;
  geocoder: any;
  showMap = false;
  lastResult: any;
  street = '';
  myInput = '';
  showRefreshHidden = true;
  @Output() updateData = new EventEmitter<GeoPlace>();
  private _resolveArr: Array<any> = [];
  private _promArr: Array<Promise<any>> = [new Promise((resolve) => {
    this._resolveArr.push(resolve);
  }), new Promise((resolve) => {
    this._resolveArr.push(resolve);
  }), new Promise((resolve) => {
    this._resolveArr.push(resolve);
  })];

  constructor(private toastCtrl: ToastController, public patientDataProvider: PatientDataProvider, 
    private events: Events, private translate:TranslateService) {
  }

  _activeItem: GeoPlace = new GeoPlace();

  @Input() set activeItem(data: GeoPlace) {
    this._activeItem = data;
    this._resolveArr[0]();
  }

  _fallbackAddress: AddressData = new AddressData();

  @Input() set fallbackAddress(data: AddressData) {
    this._resolveArr[1]();
    this._fallbackAddress = data;
  }

  _management: ManagementData = new ManagementData();

  @Input() set management(data: ManagementData) {
    this._resolveArr[2]();
    this._management = data;
  }

  ngOnInit() {
    Promise.all(this._promArr).then(() => {
      this.initView();
    });

    this.events.subscribe('set:Location', (data) => {
      if (this.patientDataProvider.activePatientData.transmitterId === data.transmitterId) {
        const geo = [data.smsText[2].replace('Lat:', ''), data.smsText[3].replace('Lon:', '')];
        this.placeMarker({lat: parseFloat(geo[0]), lng: parseFloat(geo[1])}, this.map);
        this._showMessage(this.translate.instant('Massage.LocationChange'));
      }
    });
  }

  setPlaceIntoData() {
    this._activeItem.lat = this.lastResult.geometry.location.lat();
    this._activeItem.lng = this.lastResult.geometry.location.lng();
    this._activeItem.street = this.lastResult.address_components[1].long_name + ' ' + this.lastResult.address_components[0].long_name;
    this._activeItem.zip = this.lastResult.address_components[5].long_name;
    this._activeItem.city = this.lastResult.address_components[2].long_name;

    this.updateData.emit(this._activeItem);

    this._showMessage(this.translate.instant('Message.TakeoverLocation'));
  }

  clearMarkers() {
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].setMap(null);
    }
  }

  removeMarker() {
    this.clearMarkers();
    this.markers = [];
  }

  codeAddress(address) {
    this.geocoder.geocode({address}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        this.placeMarker({lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng()}, this.map).then(() => {
          this.setPlaceIntoData();
        });
      } else {
        console.log(this.translate.instant('Error')+' ' + status);
      }
    });
  }

  placeMarker(position: any, map: any) {

    return new Promise((resolve) => {
      this.removeMarker();

      const marker = new google.maps.Marker({
        position,
        map
      });

      map.panTo(position);

      let infoWindow = new google.maps.InfoWindow({
        content: `<span>${this.translate.instant('CurrentLocation')}</span>`
      });

      this.geocoder.geocode({location: position}, (results: any, status: any) => {
        if (status === 'OK') {
          infoWindow = new google.maps.InfoWindow({
            content: '<span>' + results[0].formatted_address + '</span>'
          });


          this.lastResult = results[0];
          this.street = this.lastResult.address_components[1].short_name + ' ' + this.lastResult.address_components[0].short_name;

          infoWindow.open(this.map, marker);
          resolve(results[0]);
        }
      });

      marker.addListener('click', () => {
        infoWindow.open(this.map, marker);
      });

      this.markers.push(marker);


    });
  }

  async initMap(position: any) {
    this.geocoder = new google.maps.Geocoder();
    const mapEle = this.mapElement.nativeElement;

    const pyrmont = new google.maps.LatLng(position.latitude, position.longitude);

    this.map = new google.maps.Map(mapEle, {
      center: pyrmont,
      zoom: 17
    });

    const search = await this.searchbar.getInputElement();
    const setButton = this.setButton.nativeElement;
    const refreshButton = this.refreshButton.nativeElement;

    this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(setButton);

    //  if(this._management.deviceType.indexOf("AMG") !== -1 || this._management.deviceType.indexOf("NEMO") !== -1) {
    this.showRefreshHidden = false;
    this.map.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(refreshButton);
    //  }

    const searchBox = new google.maps.places.SearchBox(search);

    this.map.addListener('bounds_changed', () => {
      searchBox.setBounds(this.map.getBounds());
    });

    this.placeMarker({lat: position.latitude, lng: position.longitude}, this.map);

    this.map.addListener('click', (e: any) => {
      this.placeMarker(e.latLng, this.map);
    });


    searchBox.addListener('places_changed', () => {
      const places = searchBox.getPlaces();

      if (places.length === 0) {
        return;
      }

      this.removeMarker();

      // For each place, get the icon, name and location.
      const bounds = new google.maps.LatLngBounds();
      places.forEach((place: any) => {
        if (!place.geometry) {
          console.log('Returned place contains no geometry');
          return;
        }

        this.placeMarker(place.geometry.location, this.map);

        if (place.geometry.viewport) {
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
      });
      this.map.fitBounds(bounds);
    });


    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      mapEle.classList.add('show-map');
      this.showMap = true;


      if (this.myInput) {
        this.codeAddress(this.myInput);

      }

    });

  }

  refreshGeoData() {
    this.patientDataProvider.api.post('sendSMS', {
      body: {
        deviceFonNumber: this._management.deviceFonNumber,
        smsText: 'LOC',
        deviceType: this._management.deviceType
      }
    }).subscribe((data) => {
      this._showMessage(this.translate.instant('Message.RequireLocation'));
    });
  }

  initView() {
    if (!this._activeItem.lat) {
      this.myInput = this._fallbackAddress.zip + ' ' + this._fallbackAddress.city + ' ' + this._fallbackAddress.street;

      Geolocation.getCurrentPosition().then((resp) => {
        this.initMap({latitude: resp.coords.latitude, longitude: resp.coords.longitude});
      }).catch((error) => {
        this.initMap({latitude: 52.4138296, longitude: 12.5196294});
        console.log('Error getting location', error);
      });
    } else {
      this.initMap({latitude: this._activeItem.lat, longitude: this._activeItem.lng});
    }
  }

  private async _showMessage(message: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 5000,
      position: 'top'
    });
    toast.present();
  }

}
